import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Sun, Moon, Clock, Home, MessageSquare } from 'lucide-react';
import { THEMES, DEFAULT_LOGO_URL } from '../components/landing/landingConstants';
import { Button } from '../components/ui/button';

/**
 * Branded HTTP 429 page — "VAR Review."
 *
 * Shown when the rate limiter trips (login brute force protection, AI plan
 * throttle, etc.). Accepts an optional `?retry=NN` query param holding the
 * server's `Retry-After` seconds for a live countdown.
 *
 * Wire-up note for future agent: add an Axios response interceptor that on
 * `status === 429` redirects to `/var-review?retry=<Retry-After>`.
 */
export default function RateLimitedPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialRetry = Math.max(0, parseInt(params.get('retry'), 10) || 60);
  const [secondsLeft, setSecondsLeft] = useState(initialRetry);
  const [theme, setTheme] = useState(() => localStorage.getItem('landingTheme') || 'dark');
  const isDark = theme === 'dark';
  const t = THEMES[theme];

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Countdown — ticks down to 0 then stops.
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [secondsLeft]);

  const toggleTheme = () => {
    const next = isDark ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('landingTheme', next);
  };

  const canRetry = secondsLeft === 0;

  return (
    <div
      className={`min-h-screen ${t.bg} ${t.text} relative overflow-hidden`}
      data-testid="rate-limited-page"
    >
      <style>{`
        @keyframes ref-pulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.03); }
        }
        .ref-img { animation: ref-pulse 3.2s ease-in-out infinite; }

        /* Cool broadcast-blue pitch stripes — VAR / TV studio vibe */
        .pitch-stripe-blue::before {
          content: "";
          position: absolute;
          inset: 0;
          background: repeating-linear-gradient(
            90deg,
            rgba(59, 130, 246, 0.06) 0px,
            rgba(59, 130, 246, 0.06) 60px,
            transparent 60px,
            transparent 120px
          );
          pointer-events: none;
        }
      `}</style>

      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        className={`fixed bottom-6 left-6 z-50 p-3 rounded-full shadow-lg transition-all ${
          isDark ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700' : 'bg-[#007a33] text-white hover:bg-[#005c26]'
        }`}
        aria-label="Toggle theme"
        data-testid="theme-toggle"
      >
        {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>

      {/* Logo top-left */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 z-20 flex items-center gap-2 group"
        data-testid="rate-limited-logo"
      >
        <img
          src={DEFAULT_LOGO_URL}
          alt="Grassroots Coaching"
          className="w-10 h-10 object-contain group-hover:opacity-80 transition-opacity"
        />
        <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'} hidden sm:inline`}>
          Grassroots Coaching
        </span>
      </button>

      <main className="relative pitch-stripe-blue min-h-screen flex items-center justify-center px-4 py-20">
        <div className="max-w-3xl w-full text-center relative z-10">
          {/* Pre-headline label */}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 mb-6">
            <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
            <span className="text-xs font-semibold tracking-widest uppercase text-blue-500">
              Match report · 429
            </span>
          </div>

          {/* Ref hands above head — VAR signal */}
          <div className="flex items-center justify-center mb-8 select-none">
            <img
              src="https://customer-assets.emergentagent.com/job_b281913e-4091-4a90-899a-c0e767ce9196/artifacts/er90pagn_error-var.png"
              alt="Referee signalling a VAR review"
              className="w-[260px] h-[260px] sm:w-[320px] sm:h-[320px] object-contain ref-img"
              draggable="false"
              data-testid="rate-limited-referee-img"
            />
          </div>

          <h1 className={`text-4xl sm:text-6xl font-black tracking-tight mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            VAR Review.
          </h1>

          <p className={`text-base sm:text-lg max-w-xl mx-auto mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Hold up — the ref's pulled play back. You've made too many runs at goal in too short a time.
          </p>
          <p className={`text-sm max-w-xl mx-auto mb-8 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            Take a breather. The whistle goes again shortly.
          </p>

          {/* Countdown chip */}
          <div className={`inline-flex items-center gap-2 mb-8 px-4 py-2 rounded-md font-mono text-sm border ${
            isDark
              ? 'bg-gray-900/50 border-gray-700 text-gray-300'
              : 'bg-gray-100 border-gray-200 text-gray-700'
          }`} data-testid="rate-limited-countdown">
            <Clock className="w-4 h-4 text-blue-500" />
            {canRetry
              ? <span>Play resumed — you're good to go.</span>
              : <span>Play resumes in <span className={isDark ? 'text-white font-bold' : 'text-gray-900 font-bold'}>{secondsLeft}s</span></span>
            }
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              size="lg"
              onClick={() => window.history.length > 1 ? window.history.back() : navigate('/')}
              disabled={!canRetry}
              className="bg-[#007a33] hover:bg-[#005c26] text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="rate-limited-retry-btn"
            >
              {canRetry ? 'Get back on the pitch' : `Wait ${secondsLeft}s…`}
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/')}
              className={isDark ? 'border-gray-700 text-white hover:bg-gray-800' : ''}
              data-testid="rate-limited-home-btn"
            >
              <Home className="w-4 h-4 mr-2" />
              Back to the touchline
            </Button>
          </div>

          <p className={`text-xs mt-10 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            Stuck on the bench?{' '}
            <button
              onClick={() => window.location.href = 'mailto:support@grassrootscoaching.app?subject=Locked%20out%20-%20rate%20limit'}
              className="inline-flex items-center gap-1 text-[#007a33] hover:underline font-medium"
              data-testid="rate-limited-support-link"
            >
              <MessageSquare className="w-3 h-3" />
              Have a word with the gaffer
            </button>
          </p>
        </div>
      </main>
    </div>
  );
}
